import React, { FC, useEffect, useState } from 'react';
import { Formik, FormikHelpers } from 'formik';
import toast from 'react-hot-toast';
import { useUser } from '../../../hooks/useUser';
import { isInternal } from '../../../utils';
import {
  brandUserSchema,
  ImageProps,
  initValues,
  internalUserSchema,
  IProduct,
  ISchemaValues,
} from './constants';
import {
  apiCall,
  getBrandId,
  getFormattedProduct,
  toEstimate,
  uploadSingleImage,
} from './helper';
import { ProductFormElems } from './ProductFormElems';
import { handleConflictError } from '../../../utils/errorHandler';

interface Props {
  product?: IProduct;
  onComplete?: (data: any) => void;
  brandId?: number;
  skipSubmission?: boolean;
  initialProductName?: string;
  initialBrandName?: string;
  hideBrandFields?: boolean;
}

export const ProductForm: FC<Props> = ({
  product,
  onComplete,
  brandId,
  skipSubmission,
  initialProductName,
  initialBrandName,
  hideBrandFields,
}) => {
  const { user, role, brand } = useUser();
  const [initialValues, setInitialValues] = useState<ISchemaValues>({
    ...initValues,
    productName: initialProductName || '',
    brand_name: initialBrandName || '',
  });
  const [image, setImage] = useState<ImageProps>({ source: '' });

  useEffect(() => {
    const formatted = getFormattedProduct(product, brandId || brand?.[0]?.id);
    const overrides: Partial<typeof formatted> = {};
    if (!product && initialProductName) overrides.productName = initialProductName;
    if (!product && initialBrandName) overrides.brand_name = initialBrandName;
    setInitialValues(Object.keys(overrides).length ? { ...formatted, ...overrides } : formatted);
    if (product?.image) setImage({ source: product.image, error: '' });
  }, [product, brandId, initialProductName, initialBrandName]);

  const handleSubmit = async (
    values: ISchemaValues,
    { setSubmitting, setFieldError }: FormikHelpers<ISchemaValues>
  ) => {
    setSubmitting(true);
    let finalBrandId = brandId;

    try {
      finalBrandId = getBrandId(values);
    } catch (error) {
      setFieldError('brand_id', (error as Error)?.message);
      setSubmitting(false);
      return;
    }

    try {
      const payload = {
        name: values.productName,
        short_description: values.productDescription,
        brands: [
          { id: finalBrandId! },
          // Dedupe: when the main brand IS a network (RMN-only products),
          // don't send it twice.
          ...(values.rmn_ids
            ?.filter((id) => Number(id) !== finalBrandId)
            .map((id) => ({ id: Number(id) })) || []),
        ],
        url: values.url,
        product_category_ids: values.product_category_ids || [],
        image: await uploadSingleImage(image, user?.userid),
        // Returns estimate (PDP Autopilot). Omitted when not a number.
        estimated_annual_units: toEstimate(values.estimated_annual_units),
        estimated_annual_returns: toEstimate(values.estimated_annual_returns),
        disclaimers: values.disclaimers || '',
      };

      const response = await apiCall({
        isInternalUser: isInternal(role),
        payload,
        productId: product?.desireid,
      });

      const { status, data } = response;
      onComplete?.(data);
      if (status === 200 || status === 201) {
        toast.success(
          `Product ${!product?.desireid ? 'created' : 'updated'} successfully!`
        );
      }
    } catch (error: any) {
      if (!handleConflictError(error)) {
        const errorMessage =
          error?.response?.data?.message ||
          error?.message ||
          'Failed to create product';
        toast.error(errorMessage);
      }
     
    }
    setSubmitting(false);
  };

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      validationSchema={
        isInternal(role) || brand?.some((b: any) => b?.rmn)
          ? internalUserSchema
          : brandUserSchema
      }
      onSubmit={handleSubmit}
    >
      {({ setFieldValue }) => (
        <ProductFormElems
          product={product}
          setFieldValue={setFieldValue}
          image={image}
          setImage={setImage}
          hideBrandFields={hideBrandFields}
        />
      )}
    </Formik>
  );
};
